import React, { useCallback, useEffect, useState } from "react";
import api from "../../services/api";
import toast from "react-hot-toast";

type JoinRequest = {
  id: string;
  created_at: string;
  team: { id: string; name: string; age_group: string | null } | null;
  athlete: {
    id: string;
    full_name: string | null;
    login: string;
    age_group: string | null;
    guardian: { name?: string; email?: string } | null;
  };
};

/**
 * Children waiting to be let into the coach's own teams.
 *
 * The coach is the one who knows whether this child belongs in this group, and
 * they are holding a phone at the side of a pitch — not sitting at the club
 * admin's desk. The server already scopes the queue to the teams they manage,
 * so this shows them exactly their own decisions.
 *
 * Renders nothing when there is nothing to decide: a queue that is always on
 * screen stops being noticed.
 */
export default function CoachJoinRequests({
  clubId,
  teamId,
}: {
  clubId: string | null | undefined;
  /** Narrow to one team, for the team screen. */
  teamId?: string;
}) {
  const [requests, setRequests] = useState<JoinRequest[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    if (!clubId) return;
    try {
      const { data } = await api.get<{ requests: JoinRequest[] }>(`/join-links/requests/${clubId}`);
      const all = data.requests || [];
      setRequests(teamId ? all.filter((r) => r.team?.id === teamId) : all);
    } catch {
      // Non-fatal: the rest of the screen is still useful without it.
      setRequests([]);
    }
    setLoaded(true);
  }, [clubId, teamId]);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (id: string, action: "approve" | "reject") => {
    setBusy(id);
    try {
      await api.post(`/join-links/requests/${id}/${action}`);
      toast.success(action === "approve" ? "Sportiv acceptat în echipă." : "Cerere respinsă.");
      setRequests((prev) => prev.filter((r) => r.id !== id));
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Acțiunea nu a reușit.");
      await load();
    }
    setBusy(null);
  };

  if (!loaded || requests.length === 0) return null;

  return (
    <div className="p-4 bg-white border border-amber-200 rounded-2xl shadow-[0_3px_0_#fef3c7]">
      <div className="flex items-center gap-2">
        <span className="flex items-center justify-center w-6 h-6 text-xs font-extrabold rounded-full bg-amber-100 text-amber-700">
          {requests.length}
        </span>
        <h3 className="text-sm font-extrabold text-slate-800">
          {requests.length === 1 ? "Cerere de înscriere" : "Cereri de înscriere"}
        </h3>
      </div>

      <div className="mt-3 divide-y divide-slate-100">
        {requests.map((r) => (
          <div key={r.id} className="py-3">
            <p className="text-sm font-bold text-slate-800">
              {r.athlete.full_name || r.athlete.login}
            </p>
            <p className="text-xs text-slate-500">
              {r.team?.name || "Fără echipă"}
              {r.athlete.age_group ? ` · ${r.athlete.age_group}` : ""}
              {r.athlete.guardian?.name ? ` · ${r.athlete.guardian.name}` : ""}
            </p>
            <div className="flex gap-2 mt-2">
              <button
                type="button"
                onClick={() => act(r.id, "approve")}
                disabled={busy === r.id}
                className="flex-1 px-3 py-2 text-xs font-extrabold text-white rounded-xl bg-emerald-600 disabled:opacity-50"
              >
                {busy === r.id ? "..." : "Acceptă"}
              </button>
              <button
                type="button"
                onClick={() => act(r.id, "reject")}
                disabled={busy === r.id}
                className="px-3 py-2 text-xs font-bold bg-white border text-slate-500 border-slate-200 rounded-xl disabled:opacity-50"
              >
                Respinge
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
